import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, MapPin, Calendar, Lock, Unlock, Pencil, X, Save, Plus, Trash2, Users, Sprout, Receipt, Coins, Send, ChevronDown } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";
import NotesPanel from "../components/NotesPanel";
import AttachmentsPanel from "../components/AttachmentsPanel";

const FULL_ROLES = new Set(["super_admin", "admin", "manager"]);
const REOPEN_ROLES = new Set(["super_admin", "admin"]);
const INDIVIDUAL_ONLY = new Set(["groom", "driver"]);
const SETTLEMENT_HIDDEN = new Set(["vet", "groom", "driver", "handyman", "cleaner", "gardener", "farrier", "dentist", "chiropractor"]);

const CATEGORIES = [
  { value: "transport", label: "Transport" },
  { value: "accommodation", label: "Accommodation" },
  { value: "entry_fee", label: "Entry fee" },
  { value: "feed", label: "Feed" },
  { value: "vet", label: "Vet" },
  { value: "miscellaneous", label: "Miscellaneous" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const SPLIT_LABELS = { individual: "Individual", equal: "Equal", custom: "Custom", percentage: "Percentage" };

const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };
const fmtMoney = (n, c = "EUR") => `${c === "EUR" ? "€" : ""}${Number(n || 0).toFixed(2)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isFull = FULL_ROLES.has(role);
  const canReopen = REOPEN_ROLES.has(role);
  const settlementVisible = !SETTLEMENT_HIDDEN.has(role);
  const [params, setParams] = useSearchParams();

  const [trip, setTrip] = useState(null);
  const [horses, setHorses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [stableHorses, setStableHorses] = useState([]);
  const [stableUsers, setStableUsers] = useState([]);
  const [error, setError] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddHorse, setShowAddHorse] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [delExpense, setDelExpense] = useState(null);

  const canManageRoster = isFull && trip?.status === "open";

  const loadAll = async () => {
    setError("");
    try {
      const [t, h, s, e] = await Promise.all([
        api.get(`/trips/${tripId}`),
        api.get(`/trips/${tripId}/horses`),
        api.get(`/trips/${tripId}/staff`),
        api.get(`/trips/${tripId}/expenses`),
      ]);
      setTrip(t.data); setHorses(h.data); setStaff(s.data); setExpenses(e.data);
      if (!SETTLEMENT_HIDDEN.has(user?.role)) {
        const st = await api.get(`/trips/${tripId}/settlement`);
        setSettlement(st.data);
      }
    } catch (err) { setError(formatApiError(err)); }
  };

  const loadStableRefs = async () => {
    try {
      const [h, u] = await Promise.all([api.get("/horses"), api.get("/users")]);
      setStableHorses(h.data); setStableUsers(u.data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadAll(); loadStableRefs(); }, [tripId]);

  // ENH-017 (revised): compute whether the current user is "attending" this
  // trip — used to gate the attachments upload button in the exact same way
  // the backend gates the write endpoint.
  //   • admin/manager/super_admin: always attending
  //   • owner: page only loads if backend confirms they have a horse on the
  //     trip (GET /api/trips/{id} enforces this), so treat true whenever
  //     role=owner AND the trip loaded successfully. Extra-safety fallback:
  //     also check stableHorses (owner-scoped) against the trip's horse list.
  //   • other staff (vet/groom/driver/…): must appear in the trip_staff list.
  const isAttending = useMemo(() => {
    if (!user) return false;
    if (["admin", "manager", "super_admin"].includes(role)) return true;
    if (role === "owner") {
      if (!trip) return false;
      const myHorseIds = new Set((stableHorses || []).map((h) => h.id));
      if (myHorseIds.size === 0) return true; // trip loaded → backend already confirmed access
      return (horses || []).some((h) => myHorseIds.has(h.horse_id));
    }
    return (staff || []).some((s) => s.user_id === user.id);
  }, [user, role, trip, horses, staff, stableHorses]);

  const canAddExpense = trip && trip.status === "open" && (isFull || (staff || []).some((s) => s.user_id === user?.id));
  useEffect(() => {
    if (trip && params.get("edit") === "1" && isFull && trip.status === "open") {
      openEdit();
      setParams({});
    }
  }, [trip]);

  const openEdit = () => {
    setEditForm({
      name: trip.name, location: trip.location || "",
      start_date: trip.start_date, end_date: trip.end_date,
      notes: trip.notes || "",
    });
    setShowEdit(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/trips/${tripId}`, {
        name: editForm.name.trim(),
        location: editForm.location.trim() || null,
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        notes: editForm.notes.trim() || null,
      });
      setShowEdit(false);
      await loadAll();
    } catch (err) { setError(formatApiError(err)); }
  };

  const doClose = async () => {
    try {
      await api.post(`/trips/${tripId}/close`);
      setConfirmClose(false);
      await loadAll();
    } catch (err) { setError(formatApiError(err)); setConfirmClose(false); }
  };

  const doReopen = async () => {
    setReopening(true);
    setError("");
    try {
      await api.patch(`/trips/${tripId}/reopen`);
      setConfirmReopen(false);
      setSuccessMsg("Trip reopened successfully.");
      setTimeout(() => setSuccessMsg(""), 3000);
      await loadAll();
    } catch (err) {
      setError(formatApiError(err));
      setConfirmReopen(false);
    } finally {
      setReopening(false);
    }
  };

  const addHorse = async (horseId) => {
    try { await api.post(`/trips/${tripId}/horses`, { horse_id: horseId }); setShowAddHorse(false); await loadAll(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const removeHorse = async (horseId) => {
    try { await api.delete(`/trips/${tripId}/horses/${horseId}`); await loadAll(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const addStaff = async (uid) => {
    try { await api.post(`/trips/${tripId}/staff`, { user_id: uid }); setShowAddStaff(false); await loadAll(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const removeStaff = async (uid) => {
    try { await api.delete(`/trips/${tripId}/staff/${uid}`); await loadAll(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const doDeleteExpense = async () => {
    try { await api.delete(`/trips/expenses/${delExpense.id}`); setDelExpense(null); await loadAll(); }
    catch (err) { setError(formatApiError(err)); setDelExpense(null); }
  };

  if (!trip && !error) return <div className="se-card text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>;
  if (error && !trip) return <div className="se-card" style={{ color: "#922B21" }} data-testid="trip-detail-error">{error}</div>;
  if (!trip) return null;

  const closed = trip.status === "closed";
  const eligibleHorses = stableHorses.filter((h) => !horses.some((th) => th.horse_id === h.id));
  const eligibleStaff = stableUsers.filter((u) => !staff.some((ts) => ts.user_id === u.id) && u.id !== user?.id ? true : !staff.some((ts) => ts.user_id === u.id));

  return (
    <div data-testid="trip-detail-page">
      <button onClick={() => navigate("/trips")} className="se-btn se-btn-ghost mb-6" data-testid="trip-back">
        <ArrowLeft size={16} /> Back to trips
      </button>

      {error && <div className="se-card mb-4" style={{ color: "#922B21" }} data-testid="trip-detail-banner-error">{error}</div>}
      {successMsg && (
        <div
          className="se-card mb-4"
          style={{ background: "rgba(74,124,89,0.12)", color: "#2D5E3A" }}
          data-testid="trip-reopen-success"
        >
          {successMsg}
        </div>
      )}

      {/* Header */}
      <div className="se-card mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="se-label">Trip</div>
              <span className="se-badge" style={{
                background: closed ? "rgba(140,158,138,0.18)" : "rgba(74,124,89,0.15)",
                color: closed ? "#5B6B5B" : "#2D5E3A",
              }} data-testid="trip-status-badge">
                {closed ? "Closed" : "Open"}
              </span>
            </div>
            <h1 className="font-heading mt-1" style={{ fontSize: 28 }} data-testid="trip-name">{trip.name}</h1>
            <div className="text-sm mt-2 flex items-center gap-4 flex-wrap" style={{ color: "#8C9E8A" }}>
              {trip.location && <span className="flex items-center gap-1"><MapPin size={12} /> {trip.location}</span>}
              <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(trip.start_date)} – {fmtDate(trip.end_date)}</span>
            </div>
            {trip.notes && <p className="text-sm mt-3 whitespace-pre-wrap">{trip.notes}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            {isFull && !closed && (
              <>
                <button className="se-btn" onClick={openEdit} data-testid="trip-edit-button"><Pencil size={14} /> Edit</button>
                <button className="se-btn" style={{ background: "#C0392B" }} onClick={() => setConfirmClose(true)} data-testid="trip-close-button"><Lock size={14} /> Close trip</button>
              </>
            )}
            {closed && canReopen && (
              <button
                className="se-btn"
                onClick={() => setConfirmReopen(true)}
                data-testid="trip-reopen-button"
                title="Reopen this closed trip"
              >
                <Unlock size={14} /> Reopen trip
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="se-card" data-testid="trip-horses-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Sprout size={14} style={{ color: "#7C5C3A" }} /><h3 className="font-heading" style={{ fontSize: 18 }}>Horses</h3><span className="text-xs" style={{ color: "#8C9E8A" }}>· {horses.length}</span></div>
            {canManageRoster && (
              <button className="se-btn se-btn-ghost" style={{ padding: "4px 10px", fontSize: 13 }} onClick={() => setShowAddHorse((v) => !v)} data-testid="trip-add-horse-toggle"><Plus size={12} /> Add</button>
            )}
          </div>
          {showAddHorse && (
            <select className="se-input mb-3" defaultValue="" onChange={(e) => e.target.value && addHorse(e.target.value)} data-testid="trip-add-horse-select">
              <option value="">— Select a horse —</option>
              {eligibleHorses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          )}
          {horses.length === 0 && <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="trip-horses-empty">No horses on this trip yet.</div>}
          <div className="space-y-1">
            {horses.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg" style={{ background: "rgba(212,169,106,0.08)" }} data-testid={`trip-horse-${h.horse_id}`}>
                <div className="flex-1 min-w-0">
                  <div style={{ fontWeight: 600 }}>{h.horse_name || h.horse_id}</div>
                  {h.owner_name && <div className="text-xs" style={{ color: "#8C9E8A" }}>{h.owner_name}</div>}
                </div>
                {canManageRoster && (
                  <button className="se-btn se-btn-ghost" style={{ padding: 4 }} onClick={() => removeHorse(h.horse_id)} aria-label="Remove horse" data-testid={`trip-horse-remove-${h.horse_id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="se-card" data-testid="trip-staff-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Users size={14} style={{ color: "#4A7C59" }} /><h3 className="font-heading" style={{ fontSize: 18 }}>Staff</h3><span className="text-xs" style={{ color: "#8C9E8A" }}>· {staff.length}</span></div>
            {canManageRoster && (
              <button className="se-btn se-btn-ghost" style={{ padding: "4px 10px", fontSize: 13 }} onClick={() => setShowAddStaff((v) => !v)} data-testid="trip-add-staff-toggle"><Plus size={12} /> Add</button>
            )}
          </div>
          {showAddStaff && (
            <select className="se-input mb-3" defaultValue="" onChange={(e) => e.target.value && addStaff(e.target.value)} data-testid="trip-add-staff-select">
              <option value="">— Select staff —</option>
              {eligibleStaff.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
            </select>
          )}
          {staff.length === 0 && <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="trip-staff-empty">No staff on this trip yet.</div>}
          <div className="space-y-1">
            {staff.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg" style={{ background: "rgba(74,124,89,0.08)" }} data-testid={`trip-staff-${s.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div style={{ fontWeight: 600 }}>{s.user_name || s.user_id}</div>
                  {s.role && <div className="text-xs" style={{ color: "#8C9E8A" }}>{s.role}</div>}
                </div>
                {canManageRoster && (
                  <button className="se-btn se-btn-ghost" style={{ padding: 4 }} onClick={() => removeStaff(s.user_id)} aria-label="Remove staff" data-testid={`trip-staff-remove-${s.user_id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expenses */}
      <div className="se-card mb-6" data-testid="trip-expenses-panel">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2"><Receipt size={14} style={{ color: "#2D6E6E" }} /><h3 className="font-heading" style={{ fontSize: 18 }}>Expenses</h3><span className="text-xs" style={{ color: "#8C9E8A" }}>· {expenses.length}</span></div>
          {canAddExpense && (
            <button className="se-btn" onClick={() => setShowAddExpense(true)} data-testid="trip-add-expense-button"><Plus size={14} /> Add expense</button>
          )}
        </div>
        {expenses.length === 0 && <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="trip-expenses-empty">No expenses logged yet.</div>}
        <div className="overflow-x-auto">
          {expenses.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "#8C9E8A", textAlign: "left" }}>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">Split</th>
                  <th className="py-2 pr-3">Logged by</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => {
                  const canEditThis = !closed && (isFull || e.logged_by === user?.id);
                  return (
                    <tr key={e.id} style={{ borderTop: "0.5px solid rgba(212,169,106,0.2)" }} data-testid={`expense-row-${e.id}`}>
                      <td className="py-2 pr-3">{fmtDate(e.expense_date)}</td>
                      <td className="py-2 pr-3" style={{ fontWeight: 600 }}>{e.description}</td>
                      <td className="py-2 pr-3" style={{ color: "#8C9E8A" }}>{CATEGORY_LABEL[e.category] || e.category}</td>
                      <td className="py-2 pr-3 text-right" style={{ fontWeight: 600 }}>{fmtMoney(e.amount, e.currency)}</td>
                      <td className="py-2 pr-3"><span className="text-xs" style={{ color: "#8C9E8A" }}>{e.horse_count} horse{e.horse_count === 1 ? "" : "s"} — {SPLIT_LABELS[e.split_method]}</span></td>
                      <td className="py-2 pr-3" style={{ color: "#8C9E8A" }}>{e.logged_by_name || "—"}</td>
                      <td className="py-2 pr-3">
                        {canEditThis && (
                          <div className="flex gap-1 justify-end">
                            <button className="se-btn se-btn-ghost" style={{ padding: 4 }} onClick={() => setEditingExpense(e)} aria-label="Edit expense" data-testid={`expense-edit-${e.id}`}><Pencil size={12} /></button>
                            <button className="se-btn se-btn-ghost" style={{ padding: 4, color: "#C0392B" }} onClick={() => setDelExpense(e)} aria-label="Delete expense" data-testid={`expense-delete-${e.id}`}><Trash2 size={12} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Settlement */}
      {settlementVisible && settlement && (
        <SettlementPanel
          settlement={settlement}
          isFull={isFull}
          onPush={async (ownerId, status) => {
            try {
              await api.post(`/trips/${tripId}/settlement/push-to-billing`, { owner_id: ownerId, status });
              await loadAll();
            } catch (err) { setError(formatApiError(err)); }
          }}
        />
      )}

      {/* ENH-017: Trip notes + attachments */}
      {trip && (
        <div className="mt-6 grid grid-cols-1 gap-6">
          <NotesPanel targetType="trip" targetId={tripId} testid="trip-notes" />
          <AttachmentsPanel
            entityType="trip_note"
            targetId={tripId}
            testid="trip-attachments"
            contextLabel="trips"
            isAttending={isAttending}
          />
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <Modal title="Edit trip" onClose={() => setShowEdit(false)} testid="trip-edit-form">
          <form onSubmit={saveEdit}>
            <label className="se-label block mb-1">Name *</label>
            <input className="se-input mb-3" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="trip-edit-name" />
            <label className="se-label block mb-1">Location</label>
            <input className="se-input mb-3" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="trip-edit-location" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="se-label block mb-1">Start *</label><input type="date" className="se-input" required value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} data-testid="trip-edit-start" /></div>
              <div><label className="se-label block mb-1">End *</label><input type="date" className="se-input" required value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} data-testid="trip-edit-end" /></div>
            </div>
            <label className="se-label block mb-1">Notes</label>
            <textarea className="se-input" style={{ height: 80, padding: "10px 14px" }} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="trip-edit-notes" />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setShowEdit(false)}>Cancel</button>
              <button type="submit" className="se-btn" data-testid="trip-edit-submit"><Save size={14} /> Save</button>
            </div>
          </form>
        </Modal>
      )}

      {(showAddExpense || editingExpense) && (
        <ExpenseModal
          horses={horses}
          role={role}
          existing={editingExpense}
          onClose={() => { setShowAddExpense(false); setEditingExpense(null); }}
          onSaved={async () => { setShowAddExpense(false); setEditingExpense(null); await loadAll(); }}
          tripId={tripId}
        />
      )}

      <ConfirmDialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={doClose}
        title="Close this trip?"
        description="Expenses will be locked. Settlement remains visible."
        confirmLabel="Yes, close trip"
        testid="trip-close-confirm"
      />
      <ConfirmDialog
        open={confirmReopen}
        onClose={() => setConfirmReopen(false)}
        onConfirm={doReopen}
        title="Reopen this trip?"
        description="Are you sure you want to reopen this trip? The settlement will be reset and new expenses can be added."
        confirmLabel={reopening ? "Reopening…" : "Yes, reopen trip"}
        testid="trip-reopen-confirm"
      />
      <ConfirmDialog
        open={!!delExpense}
        onClose={() => setDelExpense(null)}
        onConfirm={doDeleteExpense}
        title="Delete this expense?"
        description="It will be moved to trash. Splits will be removed from the settlement."
        confirmLabel="Yes, delete"
        testid="expense-delete-confirm"
      />
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <div className="se-card w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>{title}</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SettlementPanel({ settlement, isFull, onPush }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="se-card" data-testid="trip-settlement-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between mb-3" style={{ cursor: "pointer" }}>
        <div className="flex items-center gap-2"><Coins size={14} style={{ color: "#D4A96A" }} /><h3 className="font-heading" style={{ fontSize: 18 }}>Settlement</h3></div>
        <div className="flex items-center gap-2">
          <span className="font-heading" style={{ fontSize: 20 }} data-testid="settlement-grand-total">{fmtMoney(settlement.grand_total, settlement.currency)}</span>
          <ChevronDown size={16} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 150ms" }} />
        </div>
      </button>
      {open && (
        <div className="overflow-x-auto">
          {settlement.rows.length === 0 ? (
            <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="settlement-empty">No splits yet. Add expenses to generate the settlement.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "#8C9E8A", textAlign: "left" }}>
                  <th className="py-2 pr-3">Horse</th>
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 pr-3">Itemised shares</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">Billing</th>
                  {isFull && <th className="py-2 pr-3"></th>}
                </tr>
              </thead>
              <tbody>
                {settlement.rows.map((r) => (
                  <tr key={r.horse_id} style={{ borderTop: "0.5px solid rgba(212,169,106,0.2)", verticalAlign: "top" }} data-testid={`settlement-row-${r.horse_id}`}>
                    <td className="py-2 pr-3" style={{ fontWeight: 600 }}>{r.horse_name || "—"}</td>
                    <td className="py-2 pr-3">{r.owner_name || "—"}</td>
                    <td className="py-2 pr-3">
                      <div className="space-y-1">
                        {r.items.map((i) => (
                          <div key={i.split_id} className="text-xs" style={{ color: "#5B6B5B" }}>
                            <span style={{ color: "#3B2F1E" }}>{i.description}</span>
                            <span style={{ color: "#8C9E8A" }}> · {fmtMoney(i.amount, settlement.currency)}{i.percentage != null ? ` (${i.percentage}%)` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right" style={{ fontWeight: 700 }} data-testid={`settlement-row-total-${r.horse_id}`}>{fmtMoney(r.total, settlement.currency)}</td>
                    <td className="py-2 pr-3">
                      <span className="se-badge" style={{
                        background: r.billing_status === "invoiced" ? "rgba(74,124,89,0.15)" : "rgba(212,169,106,0.18)",
                        color: r.billing_status === "invoiced" ? "#2D5E3A" : "#8C6A2F",
                      }} data-testid={`settlement-billing-${r.horse_id}`}>
                        {r.billing_status === "invoiced" ? "Invoiced" : r.billing_status === "mixed" ? "Mixed" : "Pending"}
                      </span>
                    </td>
                    {isFull && (
                      <td className="py-2 pr-3 text-right">
                        {r.owner_id && (
                          <button
                            className="se-btn se-btn-ghost"
                            style={{ padding: "4px 10px", fontSize: 12, opacity: r.billing_status === "invoiced" ? 0.5 : 1 }}
                            disabled={r.billing_status === "invoiced"}
                            onClick={() => onPush(r.owner_id, "invoiced")}
                            data-testid={`settlement-push-${r.horse_id}`}
                          >
                            <Send size={11} /> Push to billing
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function ExpenseModal({ horses, role, existing, onClose, onSaved, tripId }) {
  const isEdit = !!existing;
  const restrictIndividual = INDIVIDUAL_ONLY.has(role);
  const allowedMethods = restrictIndividual
    ? [{ value: "individual", label: "Individual" }]
    : [
        { value: "individual", label: "Individual" },
        { value: "equal", label: "Equal" },
        { value: "custom", label: "Custom" },
        { value: "percentage", label: "Percentage" },
      ];

  const [form, setForm] = useState(() => existing ? ({
    description: existing.description,
    category: existing.category,
    amount: String(existing.amount),
    expense_date: existing.expense_date,
    split_method: existing.split_method,
    notes: existing.notes || "",
  }) : ({
    description: "", category: "transport", amount: "",
    expense_date: todayIso(), split_method: restrictIndividual ? "individual" : "equal",
    notes: "",
  }));

  const [singleHorse, setSingleHorse] = useState(() => {
    if (existing && existing.split_method === "individual" && existing.splits[0]) return existing.splits[0].horse_id;
    return horses[0]?.horse_id || "";
  });
  const [custom, setCustom] = useState(() => {
    const base = {};
    if (existing && existing.split_method === "custom") {
      existing.splits.forEach((s) => { base[s.horse_id] = String(s.amount); });
    }
    horses.forEach((h) => { if (!(h.horse_id in base)) base[h.horse_id] = ""; });
    return base;
  });
  const [pct, setPct] = useState(() => {
    const base = {};
    if (existing && existing.split_method === "percentage") {
      existing.splits.forEach((s) => { base[s.horse_id] = String(s.percentage ?? ""); });
    }
    horses.forEach((h) => { if (!(h.horse_id in base)) base[h.horse_id] = ""; });
    return base;
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const amountNum = Number(form.amount || 0);
  const customSum = horses.reduce((s, h) => s + (Number(custom[h.horse_id]) || 0), 0);
  const pctSum = horses.reduce((s, h) => s + (Number(pct[h.horse_id]) || 0), 0);
  const customMatch = Math.abs(customSum - amountNum) < 0.005;
  const pctMatch = Math.abs(pctSum - 100) < 0.005;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      let splits = [];
      if (form.split_method === "individual") {
        if (!singleHorse) throw new Error("Select a horse");
        splits = [{ horse_id: singleHorse }];
      } else if (form.split_method === "equal") {
        splits = [];
      } else if (form.split_method === "custom") {
        if (!customMatch) throw new Error(`Split total ${customSum.toFixed(2)} must equal ${amountNum.toFixed(2)}`);
        splits = horses.map((h) => ({ horse_id: h.horse_id, amount: Number(custom[h.horse_id]) || 0 }));
      } else if (form.split_method === "percentage") {
        if (!pctMatch) throw new Error(`Percentages must total 100% (got ${pctSum.toFixed(2)})`);
        splits = horses.map((h) => ({ horse_id: h.horse_id, percentage: Number(pct[h.horse_id]) || 0 }));
      }
      const payload = {
        description: form.description.trim(),
        category: form.category,
        amount: amountNum,
        expense_date: form.expense_date,
        split_method: form.split_method,
        splits,
        notes: form.notes.trim() || null,
        currency: "EUR",
      };
      if (isEdit) await api.patch(`/trips/expenses/${existing.id}`, payload);
      else await api.post(`/trips/${tripId}/expenses`, payload);
      await onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail || e2.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <form className="se-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="expense-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>{isEdit ? "Edit expense" : "Add expense"}</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>

        <label className="se-label block mb-1">Description *</label>
        <input className="se-input mb-3" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Category</label>
            <select className="se-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="se-label block mb-1">Amount (EUR) *</label>
            <input type="number" min="0.01" step="0.01" className="se-input" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" />
          </div>
        </div>

        <label className="se-label block mb-1">Date *</label>
        <input type="date" className="se-input mb-3" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date" />

        <label className="se-label block mb-2">Split method</label>
        <div className="flex gap-2 mb-3 flex-wrap">
          {allowedMethods.map((m) => (
            <label key={m.value} className="flex items-center gap-1 text-sm cursor-pointer">
              <input type="radio" name="split_method" value={m.value} checked={form.split_method === m.value} onChange={() => setForm({ ...form, split_method: m.value })} data-testid={`expense-split-${m.value}`} />
              {m.label}
            </label>
          ))}
        </div>

        {form.split_method === "individual" && (
          <>
            <label className="se-label block mb-1">Assign to horse *</label>
            <select className="se-input mb-3" value={singleHorse} onChange={(e) => setSingleHorse(e.target.value)} required data-testid="expense-individual-horse">
              {horses.length === 0 && <option value="">— No horses on this trip —</option>}
              {horses.map((h) => <option key={h.horse_id} value={h.horse_id}>{h.horse_name}</option>)}
            </select>
          </>
        )}

        {form.split_method === "equal" && horses.length > 0 && amountNum > 0 && (
          <div className="mb-3 p-3 rounded-lg" style={{ background: "rgba(74,124,124,0.08)" }} data-testid="expense-equal-preview">
            <div className="text-xs" style={{ color: "#8C9E8A" }}>Each of {horses.length} horse{horses.length === 1 ? "" : "s"} will be charged:</div>
            <div className="font-heading mt-1" style={{ fontSize: 18 }}>{fmtMoney(amountNum / horses.length)}</div>
          </div>
        )}

        {form.split_method === "custom" && (
          <div className="mb-3 space-y-2">
            {horses.map((h) => (
              <div key={h.horse_id} className="flex items-center gap-2">
                <div className="flex-1 text-sm">{h.horse_name}</div>
                <input type="number" min="0" step="0.01" className="se-input" style={{ width: 120 }}
                  value={custom[h.horse_id] || ""}
                  onChange={(e) => setCustom({ ...custom, [h.horse_id]: e.target.value })}
                  placeholder="0.00"
                  data-testid={`expense-custom-${h.horse_id}`}
                />
              </div>
            ))}
            <div className="text-xs flex justify-between" style={{ color: customMatch ? "#2D5E3A" : "#C0392B" }} data-testid="expense-custom-running">
              <span>Running total: {fmtMoney(customSum)}</span>
              <span>Target: {fmtMoney(amountNum)}</span>
            </div>
          </div>
        )}

        {form.split_method === "percentage" && (
          <div className="mb-3 space-y-2">
            {horses.map((h) => (
              <div key={h.horse_id} className="flex items-center gap-2">
                <div className="flex-1 text-sm">{h.horse_name}</div>
                <input type="number" min="0" max="100" step="0.01" className="se-input" style={{ width: 100 }}
                  value={pct[h.horse_id] || ""}
                  onChange={(e) => setPct({ ...pct, [h.horse_id]: e.target.value })}
                  placeholder="0"
                  data-testid={`expense-pct-${h.horse_id}`}
                />
                <span className="text-xs" style={{ width: 18, color: "#8C9E8A" }}>%</span>
                <span className="text-xs w-20 text-right" style={{ color: "#8C9E8A" }}>= {fmtMoney(amountNum * (Number(pct[h.horse_id]) || 0) / 100)}</span>
              </div>
            ))}
            <div className="text-xs flex justify-between" style={{ color: pctMatch ? "#2D5E3A" : "#C0392B" }} data-testid="expense-pct-running">
              <span>Running: {pctSum.toFixed(2)}%</span>
              <span>Target: 100%</span>
            </div>
          </div>
        )}

        <label className="se-label block mb-1">Notes</label>
        <textarea className="se-input" style={{ height: 60, padding: "10px 14px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes" />

        {err && <div className="mt-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="expense-error">{String(err)}</div>}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="expense-submit"><Save size={14} /> {busy ? "Saving…" : isEdit ? "Save changes" : "Log expense"}</button>
        </div>
      </form>
    </div>
  );
}
